import { omit } from 'lodash';
import Router from 'next/router';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type AssetModalRightPanelOpenTab,
  type AssetModalRightPanelsTab,
  isAssetModalRightPanelTabOpen,
} from '~/components/AssetModalV2/assetModalRightPanelTabs';
import { getAssetModalRightPanelTabForRouterUrl } from '~/components/AssetModalV2/utils/getAssetModalRightPanelTabForRouterUrl';
import { QueryParamNames } from '~/constants/search';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { pushWithExistingQuery } from '~/utils/PathUtils';
import { subscribePushedRouterHref } from '~/utils/subscribePushedRouterHref';

export {
  ASSET_MODAL_RIGHT_PANEL_OPEN_TABS,
  type AssetModalRightPanelOpenTab,
  type AssetModalRightPanelsTab,
  isAssetModalRightPanelTabOpen,
} from '~/components/AssetModalV2/assetModalRightPanelTabs';

export type AssetModalRightPanelContextValue = {
  activeRightPanelTab: AssetModalRightPanelsTab;
  isRightPanelOpen: boolean;
  isRightPanelTabOpen: (tab: AssetModalRightPanelOpenTab) => boolean;
  showRightPanelTab: (tab: AssetModalRightPanelOpenTab) => void;
  /** Opens the panel on the last active tab, defaulting to INFO */
  showLastRightPanelTab: () => void;
  /** Restores the panel on the last active tab without updating the URL */
  restoreLastRightPanelTab: () => void;
  closeRightPanel: () => void;
};

const defaultContextValue: AssetModalRightPanelContextValue = {
  activeRightPanelTab: 'none',
  isRightPanelOpen: false,
  isRightPanelTabOpen: () => false,
  showRightPanelTab: () => {},
  showLastRightPanelTab: () => {},
  restoreLastRightPanelTab: () => {},
  closeRightPanel: () => {},
};

export const AssetModalRightPanelContext = createContext<AssetModalRightPanelContextValue>(defaultContextValue);

export const useAssetModalRightPanel = () => {
  const ctx = useContext(AssetModalRightPanelContext);

  if (ctx === defaultContextValue) {
    const error = new Error('useAssetModalRightPanel must be used within AssetModalRightPanelContextProvider');

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: 'Asset Modal right panel context not found',
      });
    }
  }

  return ctx;
};

type RememberActiveTab = (tab: AssetModalRightPanelOpenTab) => void;
type SetActiveRightPanelTab = (tab: AssetModalRightPanelsTab) => void;
type GetLastActiveTab = () => AssetModalRightPanelOpenTab;

type UseAssetModalRightPanelTabResult = {
  activeRightPanelTab: AssetModalRightPanelsTab;
  getLastActiveTab: GetLastActiveTab;
  rememberActiveTab: RememberActiveTab;
  setActiveRightPanelTab: SetActiveRightPanelTab;
};

/**
 * Tab state + route sync for this provider only. Unexported on purpose: a `use*.ts` file would
 * be a second public hook, and calling it from a consumer would create a second panel.
 *
 * The segmented control writes React state immediately, then the URL. Indirect opens (pills,
 * `goToAssetPage`) only change the URL. Shallow Next navigations often skip `routeChangeComplete`
 * or pass a stale `as`, so we apply the href `pushWithExistingQuery` actually wrote in the same
 * tick. `routeChangeComplete` remains for back/forward and tests that emit it. Never re-read
 * `Router.asPath` after a push to "confirm" the tab.
 */
const useAssetModalRightPanelTab = (): UseAssetModalRightPanelTabResult => {
  const [activeRightPanelTab, setActiveRightPanelTab] = useState<AssetModalRightPanelsTab>('info');
  const lastActiveTabRef = useRef<AssetModalRightPanelOpenTab>('info');

  const rememberActiveTab = useCallback<RememberActiveTab>((tab) => {
    lastActiveTabRef.current = tab;
  }, []);

  const getLastActiveTab = useCallback<GetLastActiveTab>(() => lastActiveTabRef.current, []);

  useEffect(() => {
    const applyTabFromRouterUrl = (routerUrl: string) => {
      setActiveRightPanelTab((currentTab) => {
        const nextTab = getAssetModalRightPanelTabForRouterUrl({
          currentTab,
          routerUrl,
        });

        if (nextTab.lastActiveTab) {
          lastActiveTabRef.current = nextTab.lastActiveTab;
        }

        return nextTab.activeRightPanelTab;
      });
    };

    applyTabFromRouterUrl(Router.asPath);

    Router.events.on('routeChangeComplete', applyTabFromRouterUrl);
    const unsubscribePushedHref = subscribePushedRouterHref(applyTabFromRouterUrl);

    return () => {
      Router.events.off('routeChangeComplete', applyTabFromRouterUrl);
      unsubscribePushedHref();
    };
  }, []);

  return {
    activeRightPanelTab,
    getLastActiveTab,
    rememberActiveTab,
    setActiveRightPanelTab,
  };
};

export type AssetModalRightPanelContextProviderProps = { children: ReactNode };

export const AssetModalRightPanelContextProvider = memo(({ children }: AssetModalRightPanelContextProviderProps) => {
  const { activeRightPanelTab, getLastActiveTab, rememberActiveTab, setActiveRightPanelTab } =
    useAssetModalRightPanelTab();

  const isRightPanelTabOpen = useCallback(
    (tab: AssetModalRightPanelOpenTab) => activeRightPanelTab === tab,
    [activeRightPanelTab],
  );

  const showRightPanelTab = useCallback(
    (tab: AssetModalRightPanelOpenTab) => {
      rememberActiveTab(tab);
      setActiveRightPanelTab(tab);

      if (tab === 'info') {
        Router.push(
          {
            pathname: Router.pathname,
            query: omit(Router.query, QueryParamNames.amTab),
          },
          undefined,
          { shallow: true },
        );
      } else {
        pushWithExistingQuery({
          newQuery: { [QueryParamNames.amTab]: tab },
          method: 'replace',
        });
      }
    },
    [rememberActiveTab, setActiveRightPanelTab],
  );

  const showLastRightPanelTab = useCallback(() => {
    showRightPanelTab(getLastActiveTab());
  }, [getLastActiveTab, showRightPanelTab]);

  const restoreLastRightPanelTab = useCallback(() => {
    setActiveRightPanelTab(getLastActiveTab());
  }, [getLastActiveTab, setActiveRightPanelTab]);

  const closeRightPanel = useCallback(() => {
    setActiveRightPanelTab('none');
  }, [setActiveRightPanelTab]);

  const contextValue = useMemo(
    () => ({
      activeRightPanelTab,
      isRightPanelOpen: isAssetModalRightPanelTabOpen(activeRightPanelTab),
      isRightPanelTabOpen,
      showRightPanelTab,
      showLastRightPanelTab,
      restoreLastRightPanelTab,
      closeRightPanel,
    }),
    [
      activeRightPanelTab,
      isRightPanelTabOpen,
      showRightPanelTab,
      showLastRightPanelTab,
      restoreLastRightPanelTab,
      closeRightPanel,
    ],
  );

  return <AssetModalRightPanelContext.Provider value={contextValue}>{children}</AssetModalRightPanelContext.Provider>;
});

AssetModalRightPanelContextProvider.displayName = 'AssetModalRightPanelContextProvider';
